import { useState } from 'react';
import { Req, Res } from '@cbo/shared-library';
import { SiteViewData } from '@cbo/shared-library/response/site-management.response';
import { PosProvider } from '@cbo/shared-library/types/posProvider';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import staleTimeSettings from '../../config/reactQueryConfig';
import { useUsers } from '../../contexts/userContext';
import { MessageResponse } from '../models/MessageResponse';
import {
  CustomReportsViewerRole,
  GetAllGlMappingsRequest,
  GlMappingFilterResponse,
  GlMappingsResponse,
  useAdminRequests,
  NotificationResponse,
  TotalUnreadNotifications,
  CustomReportSummary,
  CustomReportsDatasetOutput,
  CustomMetricsResponse,
  ExecuteCustomReportRequest,
  CustomMetric,
  OrganizationUser,
  CustomReportData,
  CustomReportShares,
  CustomReportBase,
  ReportListResponse,
  ReportDetails,
  GetScheduledReportsResponse,
} from './index';
import queryKeys from './queryKeys';
import { PredefinedReports, ScheduledReport } from '../../scheduleReports/types';

/**
 * USER DASHBOARD
 */
export const useUserDashboardConfigurationDataQuery = (
  isUserAuthenticated: boolean
): UseQueryResult<Res.Admin.DashboardConfigurationResponse> => {
  const { getUserDashboardConfigurationData } = useAdminRequests();

  return useQuery(
    queryKeys.dashboardConfiguration.dashboardConfiguration(),
    () => getUserDashboardConfigurationData(),
    {
      enabled: isUserAuthenticated,
      staleTime: staleTimeSettings.MEDIUM,
      useErrorBoundary: false,
    }
  );
};

/**
 * COMPANY LINKS
 */
export const useCompanyLinksDataQuery = (
  isUserAuthenticated: boolean,
  filterByUserRoles?: boolean,
  organizationId?: string
): UseQueryResult<Res.Firebase.Link[]> => {
  const { getAllCompanyLinks } = useAdminRequests();

  return useQuery(
    filterByUserRoles
      ? queryKeys.companyLinks.sideBarCompanyLinks(organizationId)
      : queryKeys.companyLinks.companyLinks(organizationId),
    async () => getAllCompanyLinks(filterByUserRoles),
    {
      enabled: isUserAuthenticated,
      staleTime: staleTimeSettings.MEDIUM,
      useErrorBoundary: false,
    }
  );
};

/**
 * FISCAL CALENDAR
 */
export const useFiscalCalendarDataQuery = (
  isUserAuthenticated: boolean,
  userId: string,
  organizationId?: string
): UseQueryResult<Res.Calendar.FiscalCalendar> => {
  const { getFiscalCalendarData } = useAdminRequests();

  return useQuery(queryKeys.fiscalCalendar.fiscalCalendarById(userId, organizationId), () => getFiscalCalendarData(), {
    enabled: isUserAuthenticated,
    staleTime: staleTimeSettings.MEDIUM,
    useErrorBoundary: false,
  });
};

/**
 * PAYROLL CALENDAR
 */
export const usePayrollCalendarDataQuery = (
  isUserAuthenticated: boolean,
  siteId: string
): UseQueryResult<Res.Calendar.PayrollCalendar | MessageResponse> => {
  const { getPayrollCalendarData } = useAdminRequests();

  return useQuery(queryKeys.payrollCalendar.calendarBySiteId(siteId), async () => getPayrollCalendarData(siteId), {
    enabled: !!isUserAuthenticated && !!siteId,
    staleTime: staleTimeSettings.MEDIUM,
    useErrorBoundary: false,
  });
};

export const useAllNotificationSettingsDataQuery = (
  isUserAuthenticated: boolean,
  organizationId?: string
): UseQueryResult<Res.Notifications.NotificationSettings[]> => {
  const { getAllNotificationSettingsData } = useAdminRequests();

  return useQuery(
    queryKeys.notificationSettings.allNotificationSettings(organizationId),
    async () => getAllNotificationSettingsData(),
    {
      enabled: isUserAuthenticated,
      staleTime: staleTimeSettings.MEDIUM,
      useErrorBoundary: false,
    }
  );
};

export const useNotificationSettingsDataQuery = (
  isUserAuthenticated: boolean,
  notificationSettingsId: string
): UseQueryResult<Res.Notifications.NotificationSettings> => {
  const { getNotificationSettingsData } = useAdminRequests();

  return useQuery(
    queryKeys.notificationSettings.notificationSettingsById(notificationSettingsId),
    async () => getNotificationSettingsData(notificationSettingsId),
    {
      enabled: isUserAuthenticated,
      staleTime: staleTimeSettings.MEDIUM,
      useErrorBoundary: false,
    }
  );
};

export const useAllEventTypesDataQuery = (
  isUserAuthenticated: boolean,
  organizationId?: string
): UseQueryResult<{ data: Res.Notifications.EventType[] }> => {
  const { getAllEventTypesData } = useAdminRequests();

  return useQuery(queryKeys.notificationSettings.allEventTypes(organizationId), async () => getAllEventTypesData(), {
    enabled: isUserAuthenticated,
    staleTime: staleTimeSettings.MEDIUM,
    useErrorBoundary: false,
  });
};

export const useNotificationsDataQuery = (
  isUserAuthenticated: boolean,
  pageNumber: number,
  pageSize: number,
  keys = ['notifications']
): UseQueryResult<NotificationResponse> => {
  const { getNotificationsData } = useAdminRequests();
  return useQuery(queryKeys.notifications.notifications(keys), async () => getNotificationsData(pageNumber, pageSize), {
    enabled: isUserAuthenticated,
    staleTime: staleTimeSettings.MEDIUM,
    useErrorBoundary: false,
    cacheTime: 0,
  });
};

export const useTotalUnreadNotificationsQuery = (
  isUserAuthenticated: boolean
): UseQueryResult<TotalUnreadNotifications> => {
  const { getTotalUnreadNotifications } = useAdminRequests();
  return useQuery(queryKeys.notifications.totalUnread(), async () => getTotalUnreadNotifications(), {
    enabled: isUserAuthenticated,
    staleTime: staleTimeSettings.LOW,
    useErrorBoundary: false,
  });
};

/**
 * Site Management
 */
export const useAllSitesDataQuery = (
  isUserAuthenticated: boolean,
  siteIds?: string[],
  organizationId?: string,
  useErrorBoundary = false
): UseQueryResult<Res.SiteManagement.PagedResponse<SiteViewData>> => {
  const { getAllSitesData } = useAdminRequests();
  return useQuery(queryKeys.siteManagement.allSites(siteIds, organizationId), async () => getAllSitesData(siteIds), {
    enabled: isUserAuthenticated,
    staleTime: staleTimeSettings.MEDIUM,
    useErrorBoundary,
  });
};

export const useSiteDataByIdQuery = (
  isUserAuthenticated: boolean,
  siteId: string
): UseQueryResult<Res.SiteManagement.SiteViewData> => {
  const { getSiteDetailsDataById } = useAdminRequests();

  return useQuery(queryKeys.siteManagement.siteDetailsDataById(siteId), async () => getSiteDetailsDataById(siteId), {
    enabled: !!isUserAuthenticated && !!siteId,
    staleTime: staleTimeSettings.MEDIUM,
    useErrorBoundary: false,
  });
};

export const useSiteDataByIdsQuery = (
  isUserAuthenticated: boolean,
  siteIds: string[]
): UseQueryResult<Res.SiteManagement.SiteViewData[]> => {
  const { getSiteDetailsDataByIds } = useAdminRequests();

  return useQuery(queryKeys.siteManagement.siteDataByIds(siteIds), async () => getSiteDetailsDataByIds(siteIds), {
    enabled: isUserAuthenticated && !!siteIds.length,
    staleTime: staleTimeSettings.MEDIUM,
    useErrorBoundary: false,
  });
};

/**
 * Audit Log
 */
export const useAllAuditLogDataQuery = (
  isUserAuthenticated: boolean,
  filter: Req.AuditLog.FilterRequest,
  organizationId?: string
): UseQueryResult<Res.AuditLog.LogEntry[]> => {
  const { getAllAuditLogData } = useAdminRequests();
  return useQuery(queryKeys.auditLog.allAuditLogs(filter, organizationId), async () => getAllAuditLogData(filter), {
    enabled: isUserAuthenticated,
    staleTime: staleTimeSettings.MEDIUM,
    useErrorBoundary: false,
  });
};

export const useAllAuditLogFilterQuery = (
  isUserAuthenticated: boolean,
  organizationId?: string
): UseQueryResult<Res.AuditLog.FilterResponse> => {
  const { getAllAuditLogFilters } = useAdminRequests();
  return useQuery(queryKeys.auditLog.allAuditLogFilters(organizationId), async () => getAllAuditLogFilters(), {
    enabled: isUserAuthenticated,
    staleTime: staleTimeSettings.MEDIUM,
    useErrorBoundary: false,
  });
};

/**
 * GL Mapping
 */
export const useGetAllJobCodeMappings = (body: GetAllGlMappingsRequest): UseQueryResult<GlMappingsResponse[]> => {
  const { getAllJobCodeMappings } = useAdminRequests();

  return useQuery(
    queryKeys.glMapping.jobCodeMappings(body?.dataTypes, body?.entityNames, body?.glAccountNames),
    async () => getAllJobCodeMappings(body),
    {
      staleTime: staleTimeSettings.MEDIUM,
      useErrorBoundary: false,
    }
  );
};

export const useGetGlMappingFiltersQuery = (): UseQueryResult<GlMappingFilterResponse> => {
  const { getGlMappingFilters } = useAdminRequests();

  return useQuery(queryKeys.glMapping.glMappingFilters, async () => getGlMappingFilters(), {
    staleTime: staleTimeSettings.HIGH,
    useErrorBoundary: false,
  });
};

export const useGetAllGlMappings = (
  enabled: boolean,
  body: GetAllGlMappingsRequest
): UseQueryResult<GlMappingsResponse[]> => {
  const { getAllGlMappings } = useAdminRequests();

  return useQuery(
    queryKeys.glMapping.allGlMappings(body?.dataTypes, body?.entityNames, body?.glAccountNames),
    () => getAllGlMappings(body),
    {
      enabled,
    }
  );
};

/**
 * USER PREFERENCES
 */
export const useGetUserPreferencesQuery = (orgId?: string): UseQueryResult<Res.Admin.UserPreferences> => {
  const { getUserPreferences } = useAdminRequests();

  return useQuery(queryKeys.userPreferences.preferences(orgId), () => getUserPreferences(), {
    staleTime: staleTimeSettings.MEDIUM,
    enabled: true,
  });
};

/**
 * POS
 */
export const usePosQuery = (
  isEnabled: boolean
): UseQueryResult<{ posProvider: PosProvider | null; addOns: string[] }> => {
  const { getPosTypeForOrg } = useAdminRequests();
  const user = useUsers();
  const orgId = user.org?.bslId;

  return useQuery(queryKeys.pos.pos(orgId), () => getPosTypeForOrg(), {
    enabled: isEnabled,
    useErrorBoundary: false,
    staleTime: 0,
  });
};

/**
 * CUSTOM REPORTS
 */
export const useGetCustomReportsQuery = (isEnabled: boolean): UseQueryResult<CustomReportSummary[]> => {
  const { getCustomReports } = useAdminRequests();
  const user = useUsers();
  const orgId = user.org?.bslId;

  return useQuery(queryKeys.customReports.customReports(orgId), () => getCustomReports(), {
    enabled: isEnabled,
    staleTime: staleTimeSettings.MEDIUM,
    useErrorBoundary: false,
  });
};

export const useGetCustomReportQuery = (id: number | null, siteId: string): UseQueryResult<CustomReportData> => {
  const { getCustomReport } = useAdminRequests();

  return useQuery(queryKeys.customReports.customReportById(id), () => getCustomReport(id, siteId), {
    enabled: id !== null,
    useErrorBoundary: false,
  });
};

export const useSharedCustomReportsQuery = (
  userId: string,
  userRoles: CustomReportsViewerRole[],
  isEnabled: boolean
): UseQueryResult<CustomReportSummary[]> => {
  const { getSharedCustomReports } = useAdminRequests();
  const user = useUsers();
  const orgId = user.org?.bslId;

  return useQuery(
    queryKeys.customReports.sharedCustomReports(orgId, userId, userRoles),
    () => getSharedCustomReports(userId, userRoles),
    {
      enabled: isEnabled,
      staleTime: staleTimeSettings.MEDIUM,
      useErrorBoundary: false,
    }
  );
};

export const useGetCustomReportsDatasetsQuery = (): UseQueryResult<CustomReportsDatasetOutput[]> => {
  const { getCustomReportsDatasets } = useAdminRequests();
  const user = useUsers();
  const orgId = user.org?.bslId;

  return useQuery(queryKeys.customReports.datasets(orgId), () => getCustomReportsDatasets(), {
    staleTime: staleTimeSettings.MEDIUM,
    useErrorBoundary: false,
  });
};

export const useGetCustomReportSharesQuery = (
  reportId: number | undefined,
  isEnabled: boolean
): UseQueryResult<CustomReportShares[]> => {
  const { getCustomReportShares } = useAdminRequests();

  return useQuery(
    queryKeys.customReports.reportShareDetails(reportId ?? 0),
    () => getCustomReportShares(reportId ?? 0),
    {
      enabled: isEnabled && reportId !== undefined,
      staleTime: staleTimeSettings.MEDIUM,
      useErrorBoundary: false,
    }
  );
};

export const useExecuteCustomReportQuery = (keepPreviousData = false) => {
  const { executeCustomReport } = useAdminRequests();
  const [request, setRequest] = useState<ExecuteCustomReportRequest>({
    detailsAndConfig: undefined,
    queryRequest: {
      startDate: '',
      endDate: '',
      siteIds: [],
    },
  });

  const isEnabled =
    request.queryRequest.siteIds.length > 0 &&
    !!request.detailsAndConfig &&
    request.queryRequest.startDate.length > 0 &&
    request.queryRequest.endDate.length > 0 &&
    request.queryRequest.startDate !== 'Invalid Date' &&
    request.queryRequest.endDate !== 'Invalid Date';

  const { data, isLoading } = useQuery(
    queryKeys.customReports.executeReport(request),
    () => executeCustomReport(request),
    {
      staleTime: staleTimeSettings.MEDIUM,
      enabled: isEnabled,
      useErrorBoundary: false,
    }
  );

  return {
    executeCustomReportResponse: data,
    setExecuteCustomReportRequest: setRequest,
    isExecuteCustomReportLoading: isLoading,
  };
};

/**
 * CUSTOM METRICS
 */
export const useGetAllCustomMetricsQuery = (
  page: number,
  limit: number,
  isEnabled = true
): UseQueryResult<CustomMetricsResponse> => {
  const { getAllCustomMetrics } = useAdminRequests();

  return useQuery(queryKeys.customMetrics.allCustomMetrics(page, limit), () => getAllCustomMetrics(page, limit), {
    enabled: isEnabled,
    staleTime: staleTimeSettings.MEDIUM,
    useErrorBoundary: false,
    keepPreviousData: true,
  });
};

export const useGetCustomMetricQuery = (isEnabled: boolean, id: string): UseQueryResult<CustomMetric> => {
  const { getCustomMetric } = useAdminRequests();

  return useQuery(queryKeys.customMetrics.customMetricById(id), () => getCustomMetric(id), {
    enabled: isEnabled && !!id,
    staleTime: staleTimeSettings.MEDIUM,
    useErrorBoundary: false,
  });
};

export const useGetCustomReportsByMetricQuery = (
  isEnabled: boolean,
  metricId: string
): UseQueryResult<CustomReportBase[]> => {
  const { getReportsByMetricId } = useAdminRequests();

  return useQuery(queryKeys.customMetrics.customMetricReports(metricId), () => getReportsByMetricId(metricId), {
    enabled: isEnabled && !!metricId,
    staleTime: staleTimeSettings.MEDIUM,
    useErrorBoundary: false,
  });
};

/**
 * ORGANIZATION USERS
 */
export const useOrganizationUsersQuery = (isEnabled: boolean): UseQueryResult<OrganizationUser[]> => {
  const { getOrganizationUsers } = useAdminRequests();

  return useQuery(queryKeys.organizationUsers.organizationUsers(), () => getOrganizationUsers(), {
    enabled: isEnabled,
    staleTime: staleTimeSettings.MEDIUM,
    useErrorBoundary: false,
  });
};

/**
 * PREDEFINED REPORTS
 */
export const useGetPredefinedReportsListQuery = (isEnabled: boolean): UseQueryResult<PredefinedReports> => {
  const { getPredefinedReportsList } = useAdminRequests();

  return useQuery(queryKeys.predefinedReport.predefinedReport(), () => getPredefinedReportsList(), {
    enabled: isEnabled,
    staleTime: staleTimeSettings.MEDIUM,
    useErrorBoundary: false,
  });
};

/**
 * LIST OF ALL REPORTS
 */
export const useGetReportsListQuery = (isEnabled: boolean): UseQueryResult<ReportListResponse[]> => {
  const { getAllReportsList } = useAdminRequests();

  return useQuery(queryKeys.ReportList.ReportList(), () => getAllReportsList(), {
    enabled: isEnabled,
    staleTime: staleTimeSettings.MEDIUM,
    useErrorBoundary: false,
  });
};

/**
 * GET REPORT DETAIL BY ID
 */
export const useGetReportDetailsQuery = (id: string): UseQueryResult<ReportDetails> => {
  const { getReportsById } = useAdminRequests();

  return useQuery(queryKeys.ReportList.ReportDetails(id ?? ''), () => getReportsById(id ?? ''), {
    enabled: !!id,
    staleTime: staleTimeSettings.MEDIUM,
    useErrorBoundary: false,
  });
};

/**
 * SCHEDULED REPORTS QUERY
 */
export const useScheduledReportsQuery = (isEnabled: boolean): UseQueryResult<GetScheduledReportsResponse> => {
  const { getScheduledReports } = useAdminRequests();

  return useQuery(queryKeys.scheduledReports.list(), () => getScheduledReports(), {
    enabled: isEnabled,
    staleTime: staleTimeSettings.MEDIUM,
    useErrorBoundary: false,
  });
};

/**
 * SCHEDULED REPORT BY ID QUERY
 */
export const useScheduledReportByIdQuery = (id: string, isEnabled: boolean): UseQueryResult<ScheduledReport> => {
  const { getScheduledReportById } = useAdminRequests();

  return useQuery(queryKeys.scheduledReports.details(id), () => getScheduledReportById(id), {
    enabled: isEnabled && !!id,
    staleTime: staleTimeSettings.MEDIUM,
    useErrorBoundary: false,
  });
};

/**
 * SCHEDULED REPORTS BY CUSTOM REPORT ID QUERY
 */
export const useScheduledReportsByReportIdQuery = (
  reportId: number | null,
  isEnabled: boolean
): UseQueryResult<GetScheduledReportsResponse> => {
  const { getScheduledReportsByReportId } = useAdminRequests();

  return useQuery(
    queryKeys.scheduledReports.byReportId(reportId ?? 0),
    () => getScheduledReportsByReportId(reportId as number),
    {
      enabled: isEnabled && !!reportId,
      staleTime: staleTimeSettings.MEDIUM,
      useErrorBoundary: false,
    }
  );
};
